import React, { useCallback, useEffect, useRef } from 'react';

import { ProductHitProps } from '../../containers/hits/HitWithWishlist';
import AngleRight from '../icon/regular/AngleRight';
import HeartRegular from '../icon/regular/Heart';
import HeartSolid from '../icon/solid/Heart';
import dataLayerPush from '../../../components/DataLayer';

export default function Product({
  title,
  url,
  image,
  priceFormatted,
  salePriceFormatted,
  isOnSale,
  isNew,
  isOutOfStock,
  productCode,
  defaultPrice,
  listId,
  listName,
  isInWishlist,
  onWishlistClick,
  onVisible,
}: ProductHitProps) {
  const productRef = useRef<HTMLDivElement>(null);

  const handleClick = useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const { href } = event.currentTarget;

    dataLayerPush({
      event: 'select_item',
      ecommerce: {
        send_to: 'GA4',
        item_list_id: listId,
        item_list_name: listName,
        items: [{
          item_name: title,
          item_id: productCode,
          price: defaultPrice,
        }],
      },
      eventCallback: () => {
        window.location.href = href;
      },
      eventTimeout: 2000,
    });
  }, [
    listId,
    listName,
    title,
    productCode,
    defaultPrice,
  ]);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        onVisible();
      }
    }, { threshold: 0.1 });

    if (productRef.current) {
      observer.observe(productRef.current);
    }

    const productRefCurrent = productRef.current;

    return () => {
      if (productRefCurrent) {
        observer.unobserve(productRefCurrent);
      }
    };
  }, [onVisible]);

  return (
    <div
      ref={productRef}
      className="w-full xs:w-1/2 2xl:w-1/3 p-2 lg:px-3.75 lg:py-3"
    >
      <div className="group/card-product relative text-gray-950">
        <div className="relative mb-2 md:mb-4 rounded-lg overflow-hidden">
          <div className="absolute top-0 left-0 w-full p-3 lg:p-6 flex justify-between gap-2 z-2 pointer-events-none">
            {(isNew || isOnSale) && (
              <div className="flex flex-col md:flex-row items-start grow gap-1 md:gap-1.5 flex-wrap">
                {isOnSale && (
                  <strong className="font-semibold text-xxs md:text-sm leading-none uppercase py-1 md:py-2 px-2 md:px-3 rounded-sm bg-pink-400 text-white">
                    {Craft.t('catalog', 'Sale')}
                  </strong>
                )}
                {isNew && (
                  <strong className="font-semibold text-sm leading-none uppercase py-2 px-3 rounded-sm bg-white text-pink-400">
                    {Craft.t('catalog', 'New')}
                  </strong>
                )}
              </div>
            )}
            <button
              className={`${isInWishlist ? 'active ' : ''}group/btn-favorite transition relative flex items-center justify-center w-7.5 h-7.5 bg-white/20 group-has-[a:hover]/card-product:bg-white [.active]:bg-white hover:bg-white rounded-sm text-white group-has-[a:hover]/card-product:text-gray-950 hover:text-gray-950 cursor-pointer z-10 shrink-0 ms-auto pointer-events-auto`}
              type="button"
              onClick={onWishlistClick}
            >
              <span className="sr-only">
                {isInWishlist
                  ? Craft.t('product', 'remove from wishlist')
                  : Craft.t('product', 'add to wishlist')}
              </span>
              <HeartRegular className="w-4 fill-current" />
              <HeartSolid className="absolute opacity-0 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 fill-pink-400 group-[.active]/btn-favorite:opacity-100" />
            </button>
          </div>
          {image && (
            <picture>
              <source srcSet={`${image.transforms.productCatalogImage2x.url} 2x`} />
              <img
                className="w-full h-full object-cover aspect-4/5 group-hover/card-product:scale-105 transition-transform duration-1000"
                src={image.transforms.productCatalogImage.url}
                alt={title}
                width={image.transforms.productCatalogImage.width}
                height={image.transforms.productCatalogImage.height}
              />
            </picture>
          )}
          <span className="absolute bottom-3 lg:bottom-6 right-3 lg:right-6 opacity-0 group-hover/card-product:opacity-100 rounded-sm w-0 group-hover/card-product:w-11 lg:group-hover/card-product:w-14 h-0 group-hover/card-product:h-11 lg:group-hover/card-product:h-13.5 bg-pink-400 text-white flex justify-center items-center transition-all">
            <AngleRight className="w-2.5 fill-current" />
          </span>
        </div>
        <div className="lg:flex lg:gap-3 lg:justify-between px-1">
          <h3 className="text-gray-400 mb-0 text-base lg:text-2xl two-line group-has-[a:hover]/card-product:text-gray-950">
            <a
              className="no-underline before:absolute before:inset-0"
              href={url}
              onClick={handleClick}
            >
              {title}
            </a>
          </h3>
          <div className="lg:text-right whitespace-nowrap">
            <div className="flex gap-2 lg:justify-end items-center lg:items-start">
              {isOnSale && (
                <span className="text-gray-400 text-sm lg:text-base line-through font-semibold lg:mt-0.5">
                  {priceFormatted}
                </span>
              )}
              <strong className="text-base lg:text-2xl font-bold">{salePriceFormatted}</strong>
            </div>
            {isOutOfStock && (
              <span className="block text-pink-400 text-sm lg:text-base leading-none">
                {Craft.t('catalog', 'Temporarily sold out')}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
